/**
 * THE-FIRST-BUILD-RUNS-STRAIGHT-THROUGH-1 — the first build builds.
 *
 * Ruled 16 September, Base44's process: after signup Ghost builds straight
 * through, narrated as it goes, with no approval gate. So the first build page
 * does not hand the person a plan to approve; it approves the plan it was given,
 * once, and watches the build to its end.
 *
 * WHAT WATCHING MEANS, AND WHY IT IS NOT THE EVENT STREAM. The approve branch
 * of assistant-plan runs its steps detached with a silent sink and answers
 * `running` at once, so a stream opened to approve carries no step events. The
 * words a person can watch are the build's life on the plan row, which is what
 * the panel and the building page watch too.
 *
 * DECIDES, AND FETCHES NOTHING. The plan status, the approve answer, the
 * watched life and the storage are handed in.
 */

/** Said when a build ended without being built. The only sentence this adds. */
export const COULD_NOT_FINISH = 'Ghost could not finish building that.'

/** How long to wait before reading a plan again, the watch contract's own pace. */
export const READ_AGAIN_MS = 2000

/** How many times to read a plan that stays proposed before calling it stuck. */
export const READ_AGAIN_TIMES = 15

/** Where a finished first build lands, until the menu names the built thing. */
export const LANDING_AFTER_BUILD = '/dashboard'

export type Move = 'approve' | 'wait' | 'watch' | 'land' | 'fail'

/**
 * WHAT TO DO WITH A PLAN, FROM ITS STATUS.
 *
 * `claimedElsewhere` is another load of this page already approving it: that
 * load approves, and this one waits and reads the plan again. It cannot watch
 * yet, because a watcher reads a plan that is not running as one that ended.
 */
export function moveFor(status: string | null, claimedElsewhere: boolean): Move {
  switch (status) {
    case 'proposed':
      return claimedElsewhere ? 'wait' : 'approve'
    case 'approved':
    case 'running':
    case 'blocked_on_merchant':
      return 'watch'
    case 'completed':
      return 'land'
    default:
      return 'fail'
  }
}

/**
 * WHAT AN APPROVE ANSWER MEANS.
 *
 * `running` is the build under way. Anything else is not a refusal to show:
 * the server refuses a second approve of a plan that is no longer proposed, so
 * the honest next step is to read the plan again and follow its status.
 */
export function afterApprove(answer: unknown): 'watch' | 'recheck' {
  const a = answer as { ok?: unknown; state?: unknown } | null
  return a !== null && a !== undefined && a.ok === true && a.state === 'running'
    ? 'watch'
    : 'recheck'
}

// ─── One approve per plan, across every load of the page ──────────────────

/** Where a load writes that it is approving a plan. */
export const CLAIM_KEY = 'ghost.firstbuild.approving.v1'

/** How long a claim stands. A load that died does not hold the plan forever. */
export const CLAIM_MS = 2 * 60 * 1000

interface Store {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * TAKE THE RIGHT TO APPROVE THIS PLAN, or learn that another load has it.
 *
 * True means this load approves. False means a fresh claim for the same plan
 * already stands. Storage that throws gives the right to this load: the
 * server's own guard still refuses a second approve.
 */
export function claimApproval(store: Store, planId: string, now: number): boolean {
  try {
    const raw = store.getItem(CLAIM_KEY)
    // A claim with no time is not a claim: `now - at` is not a number, so it
    // is never under the limit and the load takes the plan.
    const held = raw ? (JSON.parse(raw) as { planId?: unknown; at?: number }) : null
    if (held && held.planId === planId && now - Number(held.at) < CLAIM_MS) {
      return false
    }
    store.setItem(CLAIM_KEY, JSON.stringify({ planId, at: now }))
    return true
  } catch {
    return true
  }
}
